// Maps primarily to backend/routers/group.py (activity) and backend/routers/settlement.py (settlement status)
import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { env } from '../utils/env';
import { ActivityResponse, PaginatedActivityResponse } from '../types/group';

// Debug utility
const DEBUG = env.DEBUG_MODE;
const debugLog = (...args: any[]) => {
  if (DEBUG) {
    console.log(...args);
  }
};

// Cache for storing ETags
const etagCache = new Map<string, string>();

interface ActivityMetadata {
  seen_by?: string[];
  expense_id?: string;
  expense_title?: string;
  expense_amount?: number;
  currency_code?: string;
  member_id?: string;
  member_name?: string;
  comment_id?: string;
  comment_text?: string;
  settlement_id?: number;
  balance_changes?: Array<{
    user_id: string;
    amount: number;
    currency_code: string;
  }>;
}

interface ActivityEntry {
  id: string;
  type: 'expense_created' | 'expense_updated' | 'expense_deleted' | 'comment_added' | 'member_joined' | 'member_left' | 'settlement_created';
  text: string;
  created_at: string;
  user_id: string | null;
  user_name?: string;
  user_avatar?: string;
  activity_metadata: ActivityMetadata;
}

export const useGroupActivity = (groupId: string, accessToken: string, autoFetch: boolean = true, options: { isVisible?: boolean } = {}) => {
  const { isVisible = true } = options;
  const queryClient = useQueryClient();
  const queryEnabled = isVisible && autoFetch && !!accessToken && !!groupId;

  // References to track mount and dependencies
  const isMountedRef = useRef(true);
  const onlineListenerAddedRef = useRef(false);

  // Define query key
  const queryKey = ['group-activity', groupId];

  // Handler for network reconnection
  const handleOnline = useCallback(() => {
    console.log('[useGroupActivity] Network connection restored, restarting polling');
    // Remove the listener since we're handling the event now
    window.removeEventListener('online', handleOnline);
    onlineListenerAddedRef.current = false;
    // Refresh the query
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  // Helper function to enhance activities with settlement status
  const enhanceActivitiesWithSettlementStatus = useCallback(async (activities: ActivityResponse[]) => {
    if (!activities?.length) return [];

    const settlementActivities = activities.filter(a =>
      a.type === 'settlement_created' && a.activity_metadata?.settlement_id);

    if (settlementActivities.length === 0) return activities;

    console.log(`[useGroupActivity] Enhancing ${settlementActivities.length} settlement activities with status`);

    try {
      // Fetch all settlements to get their statuses
      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/settlements?include_cancelled=true`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        console.warn('[useGroupActivity] Failed to fetch settlements for status enhancement');
        return activities;
      }

      const settlements = await response.json();
      console.log(`[useGroupActivity] Fetched ${settlements.length} settlements for status enhancement`);

      // Create a map of settlement ID to status
      const settlementStatusMap = settlements.reduce((map: { [key: number]: string }, s: any) => {
        map[s.id] = s.status;
        return map;
      }, {});

      // Enhance activities with settlement status
      return activities.map((activity: ActivityResponse) => {
        if (activity.type === 'settlement_created' && activity.activity_metadata?.settlement_id) {
          const settlementId = activity.activity_metadata.settlement_id;
          const status = settlementStatusMap[settlementId];

          if (status) {
            return {
              ...activity,
              activity_metadata: {
                ...activity.activity_metadata,
                status
              }
            };
          }
        }
        return activity;
      });
    } catch (err) {
      console.error('[useGroupActivity] Error enhancing activities with settlement status:', err);
      return activities;
    }
  }, [groupId, accessToken]);

  // Main query to fetch activities
  const {
    data: queryData,
    isLoading: loading,
    error: queryError,
    refetch
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!accessToken || !groupId) {
        throw new Error('Missing required parameters: accessToken or groupId');
      }

      try {
        // Create default params
        const params = new URLSearchParams({
          page: '1',
          size: '20',
        });

        const headers: Record<string, string> = {
          'Authorization': `Bearer ${accessToken}`,
        };

        // Add If-None-Match header if we have a cached ETag
        const cacheKey = `group_activity_${groupId}`;
        const cachedETag = etagCache.get(cacheKey);
        if (cachedETag) {
          headers['If-None-Match'] = cachedETag;
        }

        const response = await fetch(`${env.API_URL}/api/groups/${groupId}/activity?${params.toString()}`, {
          headers,
          cache: 'no-cache', // Forces validation with server
        });

        // If we get a 304 Not Modified, use the cached data
        if (response.status === 304) {
          console.log(`Using cached group activity data for group ${groupId} (304 Not Modified)`);
          const cachedData = queryClient.getQueryData<{
            activities: ActivityResponse[];
            currentPage: number;
            totalPages: number;
            totalItems: number;
            pageSize: number;
          }>(queryKey);

          if (cachedData) {
            return cachedData;
          }
        }

        // Store the new ETag for future requests
        const newETag = response.headers.get('ETag');
        if (newETag) {
          etagCache.set(cacheKey, newETag);
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch activities: ${response.status}`);
        }

        const data: PaginatedActivityResponse = await response.json();

        // Enhance activities with settlement status
        const enhancedActivities = await enhanceActivitiesWithSettlementStatus(data.items);

        return {
          activities: enhancedActivities,
          currentPage: data.page,
          totalPages: data.pages,
          totalItems: data.total,
          pageSize: data.size
        };
      } catch (err) {
        console.error('Error fetching group activities:', err);
        throw err;
      }
    },
    enabled: queryEnabled,
    staleTime: 60000, // 1 minute
  });

  // Mark as seen mutation
  const markAsSeenMutation = useMutation({
    mutationFn: async (beforeId?: number) => {
      if (!accessToken || !groupId) {
        throw new Error('Missing required parameters');
      }

      await fetch(`${env.API_URL}/api/groups/${groupId}/activity/mark-seen`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(beforeId ? { before_id: beforeId } : {}),
      });

      return true;
    },
    onSuccess: () => {
      // Update the activities in the cache to mark them as seen
      queryClient.invalidateQueries({ queryKey });
    },
  });

  // Extract data from query result with defaults
  const activities = queryData?.activities || [];
  const currentPage = queryData?.currentPage || 1;
  const totalPages = queryData?.totalPages || 0;
  const totalItems = queryData?.totalItems || 0;
  const pageSize = queryData?.pageSize || 20;

  // Convert React Query error to string format for API compatibility
  const error = queryError ? (queryError instanceof Error ? queryError.message : String(queryError)) : null;

  // Maintain API compatibility
  const refresh = useCallback((page: number = 1) => {
    // Update the query with new parameters
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        currentPage: page
      };
    });
    refetch();
  }, [refetch, queryClient, queryKey]);

  const handlePageChange = useCallback((newPage: number) => {
    // Update the current page in the query data
    queryClient.setQueryData(queryKey, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        currentPage: newPage
      };
    });
    refetch();
  }, [refetch, queryClient, queryKey]);

  const markAsSeen = useCallback(async (beforeId?: number) => {
    return markAsSeenMutation.mutateAsync(beforeId);
  }, [markAsSeenMutation]);

  // Add online event listener if needed
  useEffect(() => {
    if (typeof window !== 'undefined' && !onlineListenerAddedRef.current) {
      window.addEventListener('online', handleOnline);
      onlineListenerAddedRef.current = true;
    }

    return () => {
      if (typeof window !== 'undefined' && onlineListenerAddedRef.current) {
        window.removeEventListener('online', handleOnline);
        onlineListenerAddedRef.current = false;
      }
    };
  }, [handleOnline]);

  // Track mount and unmount
  useEffect(() => {
    isMountedRef.current = true;
    debugLog(`[useGroupActivity] Hook mounted for group ${groupId}`);

    return () => {
      isMountedRef.current = false;
      debugLog(`[useGroupActivity] Hook unmounted for group ${groupId}`);
    };
  }, [groupId]);

  return {
    activities,
    loading,
    error,
    refresh,
    markAsSeen,
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    handlePageChange
  };
}; 